import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, ShieldCheck, Clock, Car } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";
  const { setUser } = useAuth();
  const [tab, setTab] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (tab === "signin") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(data);
        navigate(next);
        return;
      }
      await api.post("/auth/register", { name: form.name, email: form.email, password: form.password, phone: form.phone || null });
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
        navigate(next);
      } catch {
        toast.info(t("If this email is new, your account is ready — please sign in. Already have an account? Use 'Forgot your password?'."));
        setTab("signin");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-4xl bg-white rounded-lg border border-[#e2ebe4] shadow-sm overflow-hidden grid lg:grid-cols-[1fr_360px] fade-up" data-testid="auth-card">
          <div className="p-6 sm:p-10">
            <h1 className="heading-upper text-xl text-[#144733] mb-6">{tab === "signin" ? t("Welcome back.") : t("Create your account.")}</h1>

            <div className="flex rounded-md overflow-hidden border border-[#d5ddd5] mb-6">
              <button onClick={() => { setTab("signin"); setError(""); }}
                className={`flex-1 py-2.5 text-xs font-medium uppercase tracking-wide transition-colors ${tab === "signin" ? "bg-[#009900] text-white" : "bg-white text-[#144733]"}`}
                data-testid="auth-tab-signin">{t("Sign In")}</button>
              <button onClick={() => { setTab("signup"); setError(""); }}
                className={`flex-1 py-2.5 text-xs font-medium uppercase tracking-wide transition-colors ${tab === "signup" ? "bg-[#009900] text-white" : "bg-white text-[#144733]"}`}
                data-testid="auth-tab-signup">{t("Sign Up")}</button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {tab === "signup" && (
                <>
                  <div>
                    <label className="field-label">{t("Full name")}</label>
                    <input className="field-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" />
                  </div>
                  <div>
                    <label className="field-label">{t("Phone (optional)")}</label>
                    <input className="field-input" placeholder="+971 5X XXX XXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone-input" />
                  </div>
                </>
              )}
              <div>
                <label className="field-label">{t("Email")}</label>
                <input type="email" className="field-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
              </div>
              <div>
                <label className="field-label">{t("Password")}</label>
                <input type="password" className="field-input" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
              </div>
              {tab === "signin" && (
                <p className="text-right">
                  <Link to="/forgot-password" className="text-xs text-[#009900] underline" data-testid="forgot-password-link">{t("Forgot your password?")}</Link>
                </p>
              )}
              {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="auth-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-cta rounded w-full py-3 text-sm flex items-center justify-center gap-2" data-testid="login-form-submit-button">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {tab === "signin" ? t("Sign In") : t("Create Account")}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#e2ebe4]" />
              <span className="text-[10px] uppercase tracking-widest text-[#57685e]">{t("or")}</span>
              <div className="flex-1 h-px bg-[#e2ebe4]" />
            </div>

            <button onClick={googleLogin} className="w-full border-2 border-[#144733] rounded py-3 text-sm font-medium uppercase tracking-wide text-[#144733] hover:bg-[#f0f5f0] transition-colors flex items-center justify-center gap-2" data-testid="google-login-btn">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
              {t("Continue with Google")}
            </button>

            <p className="text-[11px] text-[#8a978f] mt-5">
              {t("By continuing you accept the")} <Link to="/terms" className="underline">{t("rental terms")}</Link>{t(". Minimum driver age 21.")}
            </p>
          </div>

          <aside className="hidden lg:flex flex-col justify-between bg-[#144733] p-8 text-white" data-testid="auth-side-panel">
            <div className="promo-stripe">
              <h2 className="heading-upper text-lg text-white">{t("Your rentals, in one place.")}</h2>
              <p className="text-white/75 text-xs mt-2 leading-relaxed">{t("Extend, swap, download invoices and manage every charge — self-service, no phone queue.")}</p>
            </div>
            <ul className="space-y-4 text-xs text-white/85 mt-8">
              <li className="flex gap-2.5"><Car className="w-4 h-4 text-[#FFDC00] shrink-0" /> {t("A wide fleet with zero-deposit options")}</li>
              <li className="flex gap-2.5"><Clock className="w-4 h-4 text-[#FFDC00] shrink-0" /> {t("Delivery in as little as 4 hours in Dubai & Sharjah")}</li>
              <li className="flex gap-2.5"><ShieldCheck className="w-4 h-4 text-[#FFDC00] shrink-0" /> {t("Third-party cover over 1 million USD, with CDW on every rental")}</li>
            </ul>
            <p className="text-[10px] text-white/50 mt-8">{t("No paperwork before you book — documents upload after confirmation.")}</p>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
